import {AfterViewInit, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import tabs from './overview-tabs.json';
import {Permissions} from "../../../user/permissions";
import {Product} from "../product";
import {Website} from "../website";
import {ActivatedRoute} from '@angular/router';

import * as fromStore from "../../../store";
import {Store} from '@ngrx/store';
import {User} from "../../../user/user";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {of, Subscription} from "rxjs";
import {
  UserSubscription,
  UserSubscriptionIds,
  UserSubscriptionType,
} from "../../../user/user-subscription";
import {filter, map, switchMap} from "rxjs/operators";
import {Config} from "../../../config/config";
import {TypeFormAnswers} from "../../../add-website/typeFormAnswers";
import {combineLatest} from "rxjs";
import {clientRoles} from "../../../user/user-roles-actions";
import {Utils} from "../../../shared/utils/utils";
import {HostingData} from "../hosting/info/hostingData";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {animate, style, transition, trigger} from "@angular/animations";

@Component({
    selector: 'db-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('500ms ease-in', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('500ms ease-out', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class OverviewComponent implements OnInit, AfterViewInit {
  public Config = Config;
  public website: Website;
  public hostingInfoData: HostingData;
  public backups: any;
  public permissions: Permissions;
  public isManagable: boolean;
  public loading: boolean;
  public plugins: Product[];
  public themes: Product[];
  public hosting_services: any;
  public widgetDefaultOrder: any;
  public hostingVisitorsByWebsiteData: any;
  public usedHostingStorage: number;
  public usedHostingFileStorageByWebsite: number;
  public usedHostingDbStorageByWebsite: number;
  public usedHostingVisits: number;
  public usedHostingVisitsByWebsite: number;
  public hasHostingBanner: any;
  public user: User;
  public userSubscription: UserSubscription;
  public desktopScore: number;
  public userSubscriptionIds: any = UserSubscriptionIds;
  public userSubscriptionType: any = UserSubscriptionType;
  public isMobile: boolean;
  public extraMobile: boolean;
  public userLocalStorage: any;
  public agencyOrFreelancer: boolean;
  public typeFormAnswers = TypeFormAnswers;
  public hasDomainBuyAccess: boolean = true;
  public hasDomainAgreement: boolean = false;
  public hasBillingInfo: boolean;
  private currentWorkspaceId: number;
  public tabs: any[] = tabs;
  public activeTab: any = tabs[0].id;
  public primaryChanged: boolean;
  public demoUser: boolean;
  public hideCdnService: boolean = false;
  public highlight: boolean = !localStorage.getItem('db-higlilght-domain-widget');
  public websiteUrl: string;
  public lottieConfig: any;
  private _destroyRef: DestroyRef = inject(DestroyRef);
  @ViewChild('domainVideo') videoRef!: ElementRef<HTMLVideoElement>;

  constructor(
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(workspaceIdEntities => {
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || workspaceIdEntities.ownerWorkspaceId;
    });
  }

  ngOnInit() {
    this.lottieConfig = {
      path: 'assets/images/lotties/domain.json',
      renderer: 'svg',
      autoplay: true,
      loop: false
    };
    (document.querySelector('.db-main__content') as HTMLElement)?.click();
    this._getUserSubscription();
    this.user = this._route.snapshot.parent.data['user'];
    this.website = this._route.snapshot.parent.data['website'];
    this.websiteUrl = this.website && this.website.site_url.replace(/^https?:\/\//i, '');
    this.userSubscription = this._route.snapshot.parent.data['userSubscription'];
    this.hasDomainAgreement = this.userSubscription.hasDomainAgreement;
    this._hideCdnService();
    this.demoUser = Utils.isDemoUser(this.userSubscription, this.user);
    const userRole = this.user && this.user.workspaces.find(el => el.workspaceId === this.currentWorkspaceId);
    this.hasDomainBuyAccess = userRole ? [clientRoles.COMPANY_ADMIN, clientRoles.OWNER].includes(userRole.roleId) : false;
    this.hasBillingInfo = userRole ? [clientRoles.COMPANY_ADMIN, clientRoles.OWNER].includes(userRole.roleId) : true;
    this.desktopScore = this.website.score && this.website.score.currentScore && !TypeUtils.isNull(this.website.score.currentScore.desktopScore) ? this.website.score.currentScore.desktopScore : (this.website.score && this.website.score.previousScore && !TypeUtils.isNull(this.website.score.previousScore.desktopScore)) ? this.website.score.previousScore.desktopScore : null;
    this.hasHostingBanner = localStorage.getItem("showConnectBanner" + this.user.id);
    this.widgetDefaultOrder = this._route.snapshot.data['overviewWidgetsOrdering'];
    this.hostingInfoData = this._route.snapshot.data['hostingInfoData'];
    this.primaryChanged = !Utils.checkStringInArray(this.website.site_url, Config.hostnameList) && !this.demoUser;
    this.permissions = this._route.snapshot.parent.data['user'].permissions;
    this.hosting_services = this._route.snapshot.parent.data['user'].hosting_services;
    this.isManagable = !this.website.isLocalhost && this.permissions.canManageContent;
    this.usedHostingStorage = this.hosting_services ? this._route.snapshot.data['hostingUsageData'] : null;
    this.usedHostingDbStorageByWebsite = this.hosting_services && this._route.snapshot.data['hostingUsageByWebsiteData'] ? this._route.snapshot.data['hostingUsageByWebsiteData'].db_size : null;
    this.usedHostingFileStorageByWebsite = this.hosting_services && this._route.snapshot.data['hostingUsageByWebsiteData'] ? this._route.snapshot.data['hostingUsageByWebsiteData'].files_size : null;
    this.usedHostingVisits = this.hosting_services && this._route.snapshot.data['hostingVisitorsData'] ? this._route.snapshot.data['hostingVisitorsData'] : null;
    this.usedHostingVisitsByWebsite = this.hosting_services && this._route.snapshot.data['hostingVisitorsByWebsiteData'] ? this._route.snapshot.data['hostingVisitorsByWebsiteData'].sum : null;
    this.hostingVisitorsByWebsiteData = this.hosting_services && this._route.snapshot.data['hostingVisitorsByWebsiteData'] ? this._route.snapshot.data['hostingVisitorsByWebsiteData'] : null;
    this.addEventHandlers();
    this.isMobile = window.innerWidth <= 991;
    this.extraMobile = window.innerWidth < 670;
  }
  ngAfterViewInit() {
    (document.querySelector('.db-main__content') as HTMLElement)?.click();
    const video = this.videoRef && this.videoRef.nativeElement;
    setTimeout(() => {
      video && video.play().catch(err => {
        console.log('Autoplay failed:', err);
      });
    }, 100)

  }

  public videoEnded() {
    this.highlight = false;
    localStorage.setItem('db-higlilght-domain-widget', '1');
    setTimeout(() => {
      document.querySelector('.domains-wrap') && document.querySelector('.domains-wrap').classList.remove('animated')
    }, 5000);
  }


  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription),
        )),
        filter(([user, userSubscription]) => !!user && !!userSubscription)
      )
      .subscribe(([user, userSubscription]) => {
        this.user = user;
        this.userSubscription = userSubscription;
        this.hasDomainAgreement = userSubscription.hasDomainAgreement;
        const userRole = this.user && this.user.workspaces.find(el => el.workspaceId === this.currentWorkspaceId);
        this.hasDomainBuyAccess = userRole ? [clientRoles.COMPANY_ADMIN, clientRoles.OWNER].includes(userRole.roleId) : false;
        this.demoUser = Utils.isDemoUser(this.userSubscription, this.user);

        // this._store.dispatch(new fromStore.LoadWebsiteProducts(this.website.id));
      });
  }

  private _hideCdnService() {
    const hideCdnService = ([
        UserSubscriptionType.ONE_PRODUCT,
        UserSubscriptionType.PREMIUM,
      ].includes(this.userSubscription.type) && !this.website.domain_hash)
      || TypeUtils.isUndefined(this.userSubscription.plan.cdnService);
    if (hideCdnService
      && this.userSubscription
      && !this.userSubscription.trialBoosterClient
      && !(this.userSubscription && this.userSubscription.platformPaidClient && (this.website.domain_hash || this.website.connectedFrom === 'speed_optimizer'))
      && !this.userSubscription.trialClient) {
      this.hideCdnService = true;
    }
    console.log(this.userSubscription.plan.cdnService, this.hideCdnService);
  }

  private addEventHandlers(): void {
    this._store
      .select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean): boolean => !!loaded),
        switchMap(() => this._store.select(fromStore.getWebsite(this.website.id))),
        switchMap((website: Website | null) => {
          if (website || this.website?.id !== Config.demoDomainId) {
            return of(website);
          }
          return this._store.select(fromStore.getWebsites).pipe(
            map((websites: Website[]) => websites?.at(0))
          );
        })).subscribe((website: Website) => {
      if (website) {
        this.website = website;
        this.websiteUrl = this.website && this.website.site_url.replace(/^https?:\/\//i, '');
        this.primaryChanged = !Utils.checkStringInArray(this.website.site_url, Config.hostnameList) && !this.demoUser;
      }
    })

    combineLatest(
      this._store.select(fromStore.getUserLoaded),
      this._store.select(fromStore.getWebsitePluginsLoaded(this.website?.id))
    )
      .pipe(
        filter(([userLoaded, pluginsLoaded]) => userLoaded && pluginsLoaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getWebsiteWoocommercePlugin(this.website?.id))
        )),
        filter(([user, _]) => !!user),
      )
      .subscribe(([user, userLocalStorage, ecommercePlugin]) => {
        this.userLocalStorage = userLocalStorage;
        this.agencyOrFreelancer = user && user.agencyOrFreelancer;
      })
  }

}

